/**
 * Event tracking helpers para las landing pages.
 * Cada evento se envía EN PARALELO a Google Tag Manager (dataLayer) y a PostHog
 * (mismo project que la webapp), para unificar el embudo
 * landing → registro → uso sin perder el tracking GA4 existente.
 */

import { capture as posthogCapture } from './posthog';
import { hasAnalyticsConsent } from './cookie-consent';
import { pushAnalyticsEvent } from './google-tag-manager';

// Renombrados canónicos (Tracking Plan v1): pasamos a la convención object_action
// y corregimos el typo "leak"→"lead". Dual-write: durante la ventana de migración
// (~30 días) emitimos AMBOS nombres para no romper insights/dashboards existentes.
// Cuando los dashboards estén migrados al nombre nuevo, se retira el viejo.
const EVENT_ALIASES: Record<string, string> = {
  leak_capture_open: 'lead_capture_started',
  leak_capture_submit: 'lead_captured',
  leak_capture_skip: 'lead_capture_skipped',
};

function emitOne(eventName: string, params?: Record<string, string | number | boolean>) {
  if (!hasAnalyticsConsent()) return;
  // GA4 events are consumed by tags configured in the consent-gated GTM container.
  pushAnalyticsEvent(eventName, params);
  // PostHog en paralelo (mismo nombre de evento y props).
  posthogCapture(eventName, params);
}

function trackEvent(eventName: string, params?: Record<string, string | number | boolean>) {
  emitOne(eventName, params);
  // Dual-write del nombre canónico si el evento está en migración.
  const canonical = EVENT_ALIASES[eventName];
  if (canonical) emitOne(canonical, params);
}

// ── B2C Events (Jams) ──────────────────────────────────────

/** Waitlist modal opened */
export function trackWaitlistOpen(source: string) {
  trackEvent('waitlist_open', { source });
}

/** Waitlist form submitted successfully */
export function trackWaitlistSignup(source: string, instrument: string, level: string) {
  trackEvent('waitlist_signup', { source, instrument, level });
}

// ── B2B Events (Escuelas / Salas) ──────────────────────────

/** Contact form B2B submitted successfully */
export function trackLeadB2B(source: string) {
  trackEvent('lead_b2b_submit', { source });
}

/** Demo modal opened */
export function trackDemoOpen(source: string) {
  trackEvent('demo_open', { source });
}

/** Demo form submitted successfully */
export function trackDemoSubmit(source: string) {
  trackEvent('demo_submit', { source });
}

// ── General Events ─────────────────────────────────────────

/** WhatsApp button clicked */
export function trackWhatsAppClick(page: string) {
  trackEvent('whatsapp_click', { page });
}

/** CTA button clicked (hero, final CTA, etc.) */
export function trackCTAClick(ctaName: string, page: string) {
  trackEvent('cta_click', { cta_name: ctaName, page });
}

// ── Lead Magnet Events ─────────────────────────────────────

/** Lead magnet modal opened */
export function trackLeadMagnetOpen(leadMagnetType: string, vertical: string) {
  trackEvent('lead_magnet_open', { lead_magnet_type: leadMagnetType, vertical });
}

/** Lead magnet downloaded successfully */
export function trackLeadMagnetDownload(leadMagnetType: string, vertical: string) {
  trackEvent('lead_magnet_download', { lead_magnet_type: leadMagnetType, vertical });
}

// ── Pricing Events ────────────────────────────────────────────

/** Pricing section viewed */
export function trackPricingView(plan: string) {
  trackEvent('pricing_view', { plan });
}

/** Pricing plan selected */
export function trackPricingSelect(plan: string, price: number) {
  trackEvent('pricing_select', { plan, price });
}

// ── Directory Events ──────────────────────────────────────────

/** Directory listing viewed */
export function trackDirectoryView(listingId: string, listingType: string, city: string) {
  trackEvent('directory_listing_view', { listing_id: listingId, type: listingType, city });
}

/** Directory search performed */
export function trackDirectorySearch(query: string, resultsCount: number) {
  trackEvent('directory_search', { query, results_count: resultsCount });
}

/** Un resultado del autocomplete de búsqueda fue seleccionado → navega a la ficha */
export function trackDirectorySearchResultClick(listingId: string, listingType: string) {
  trackEvent('directory_search_result_click', { listing_id: listingId, type: listingType });
}

/** Claim profile flow started */
export function trackClaimStart(listingId: string, listingName: string) {
  trackEvent('claim_profile_start', { listing_id: listingId, listing_name: listingName });
}

/** Claim profile completed */
export function trackClaimComplete(listingId: string) {
  trackEvent('claim_profile_complete', { listing_id: listingId });
}

/** Directory contact click (phone, email, website) */
export function trackDirectoryContact(listingId: string, contactType: 'phone' | 'email' | 'website' | 'directions') {
  trackEvent('directory_contact_click', { listing_id: listingId, contact_type: contactType });
}

/** Tipos de CTA externos de una ficha del directorio que interceptamos con el modal de captura */
export type LeakRedirectType =
  | 'website' | 'whatsapp' | 'phone' | 'email'
  | 'instagram' | 'facebook' | 'youtube' | 'maps';

/** Modal de captura (leak-capture) abierto al pulsar un CTA externo de la ficha */
export function trackLeakCaptureOpen(listingId: string, redirectType: LeakRedirectType) {
  trackEvent('leak_capture_open', { listing_id: listingId, redirect_type: redirectType });
}

/** Email capturado con éxito en el modal antes de redirigir fuera */
export function trackLeakCaptureSubmit(listingId: string, redirectType: LeakRedirectType) {
  trackEvent('leak_capture_submit', { listing_id: listingId, redirect_type: redirectType });
}

/** Usuario saltó la captura ("Ir directamente") y salió sin dejar email */
export function trackLeakCaptureSkip(listingId: string, redirectType: LeakRedirectType) {
  trackEvent('leak_capture_skip', { listing_id: listingId, redirect_type: redirectType });
}

/** Card de la PLP clicada → navega a la ficha */
export function trackDirectoryCardClick(listingId: string, listingType: string) {
  trackEvent('directory_card_click', { listing_id: listingId, type: listingType });
}

/** Filtro aplicado en la PLP (tipo / rating / ciudad) */
export function trackDirectoryFilter(filter: 'type' | 'rating' | 'city', value: string) {
  trackEvent('directory_filter', { filter, value });
}

/** Mapa del directorio abierto */
export function trackDirectoryMapOpen(viewMode: string) {
  trackEvent('directory_map_open', { view_mode: viewMode });
}

/** "Ver más" (load more) en la PLP */
export function trackDirectoryLoadMore(loadedCount: number) {
  trackEvent('directory_load_more', { loaded_count: loadedCount });
}

// ── Conversion Events ─────────────────────────────────────────

/** Trial started */
export function trackTrialStart(plan: string, source: string) {
  trackEvent('trial_start', { plan, source });
}

/** Checkout initiated */
export function trackCheckoutStart(plan: string, price: number) {
  trackEvent('checkout_start', { plan, price });
}

/** Subscription completed */
export function trackSubscriptionComplete(plan: string, price: number) {
  trackEvent('subscription_complete', { plan, price, currency: 'EUR' });
}

// ── Directorio: eventos canónicos del Tracking Plan (funnel lead-gen + claim) ──
// Convención object_action; props de dominio directorio (listing_*, city, cta_type).

type DirListingCtx = {
  listing_id: string;
  listing_type?: string;
  city?: string;
  is_premium?: boolean;
  is_claimed?: boolean;
};

/** Listado (PLP) visto — top del funnel lead-gen del directorio. */
export function trackPlpViewed(params: { results_count: number; city?: string }) {
  trackEvent('plp_viewed', {
    results_count: params.results_count,
    city: params.city ?? '',
  });
}

/** Ficha (PDP) vista — paso PLP→PDP del funnel lead-gen y base del funnel claim. */
export function trackPdpViewed(ctx: DirListingCtx) {
  trackEvent('pdp_viewed', {
    listing_id: ctx.listing_id,
    listing_type: ctx.listing_type ?? '',
    city: ctx.city ?? '',
    is_premium: Boolean(ctx.is_premium),
    is_claimed: Boolean(ctx.is_claimed),
  });
}

/** Clic en un CTA de contacto externo (web/tel/whatsapp/email/redes) — intención. */
export function trackDirectoryCtaClicked(ctx: DirListingCtx, ctaType: string) {
  trackEvent('directory_cta_clicked', {
    listing_id: ctx.listing_id,
    cta_type: ctaType,
    is_premium: Boolean(ctx.is_premium),
  });
}

/** Se abre el flujo de reclamar ficha — paso 1 del funnel de monetización premium. */
export function trackClaimStarted(ctx: DirListingCtx, entryPoint: string) {
  trackEvent('claim_started', {
    listing_id: ctx.listing_id,
    listing_type: ctx.listing_type ?? '',
    entry_point: entryPoint,
  });
}

/** Reclamación enviada con éxito — activación del dueño de la ficha. */
export function trackClaimCompleted(ctx: DirListingCtx) {
  trackEvent('claim_completed', { listing_id: ctx.listing_id });
}
